import { TWidget } from 'widgets/Widget';

type TEntry = {
    intersectionRatio: number;
};

/**
 * @param Widget Base widget for extending
 * @returns StickyHeader widget
 */
function StickyHeaderClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory global
     * @class StickyHeader
     * @augments Widget
     * @classdesc StickyHeader helper. Should be on element before StickyHeader to observe intersection
     * Could be reimplemented with getBoundClientRect.top and scroll listener.
     * The main purpose is to define anchor for StickyHeader and toggle StickyHeader.
     *
     * @example <caption>Example of StickyHeader widget usage</caption>
     * <div data-widget="stickyHeader"></div>
     */
    class StickyHeader extends Widget {
        observer?: IntersectionObserver;

        prefs() {
            return {
                classesShow: 'b-header_stuck',
                dataTau: 'header_sticky',
                ...super.prefs()
            };
        }

        /**
         * @description Initialize widget logic
         */
        init() {
            const stickyHeader = this.ref('self').get();

            if (!stickyHeader) {
                return;
            }

            this.observer = new IntersectionObserver(([entry]) => this.toggleStickyState(entry), { threshold: 1 });
            this.observer.observe(stickyHeader);
        }

        /**
         * @description Toggle sticky header state
         * @param entry Anchor for apply sticky header
         */
        toggleStickyState(entry: TEntry) {
            document.body.classList.toggle(this.prefs().classesShow, entry.intersectionRatio < 1);

            if (entry.intersectionRatio < 1) {
                document.body.setAttribute('data-tau', this.prefs().dataTau);
            } else {
                document.body.removeAttribute('data-tau');
            }
        }

        /**
         * @description Destroy widget logic
         */
        destroy() {
            const stickyHeader = this.ref('self').get();

            if (this.observer && stickyHeader) {
                this.observer.unobserve(stickyHeader);
                this.observer.disconnect();
            }
        }
    }

    return StickyHeader;
}

export type TStickyHeader = ReturnType<typeof StickyHeaderClassCreator>;

export type TStickyHeaderInstance = InstanceType<TStickyHeader>;

export default StickyHeaderClassCreator;
